/*
 * The Properties box of a release note: the product and the version, saved as one
 * optimistic-concurrency unit under the note's rowversion (S42, 2026-10-12).
 *
 * THE ADDRESS IS THE PAIR (PRODUCT, VERSION). A release note has no slug: the
 * owner's answer of 2026-10-12 is one note per product and version, enforced by
 * UQ_ReleaseNote_ProductId_Version, and the pair IS the public address,
 * /releasenotes/<productSlug>/<version>. So these two fields are one decision,
 * as an article's product and slug are, and they live on kb.ReleaseNote rather
 * than on a revision - changing them is not in the change history, exactly as an
 * article's address is not (decision D-b).
 *
 * Copied from ArticleProperties and simplified rather than shared with it: no
 * categories, no addresses-in-use pre-fetch, and no inbound-link rewriting -
 * nothing links TO a release note (there is no token kind for one), so a move
 * has no rewrite to report, only the new address.
 *
 * A pair another note of the product already has is caught twice: by the server
 * before its write, which answers 409 with the message under `version` - shown
 * under the field here, not in a banner - and by the unique index itself for the
 * race the first cannot see. The server's verdict is about the pair that was
 * sent, so it clears the moment either half moves.
 *
 * NOT RE-SEEDED FROM THE PROP, for ArticleProperties' reason: saving the text
 * bumps the row's rowversion, and an effect watching it would throw away an
 * unsaved property edit. The rowversion sent is read from the prop.
 *
 * Its words come from the chosen language's dictionary, `t.admin.releaseNotes` (S48).
 */

import { useState } from 'react';
import type { AdminReleaseNoteDetail } from '@accotool-kb/shared';
import { isValidReleaseNoteVersion, releaseNoteHref } from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import { useAction } from '../../hooks/useAsync.js';
import { ErrorBanner } from '../../components/ui.js';
import type { MetadataBundle } from '../../api/adminApi.js';
import { ApiError } from '../../api/client.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';
import { ProductSelect } from './ProductSelect.js';
import { ReleaseNoteVersionInput } from './ReleaseNoteVersionInput.js';

/** The Properties card: product and version - the note's address - under the rowversion. */
export function ReleaseNoteProperties({
  note,
  metadata,
  onUpdated,
}: {
  note: AdminReleaseNoteDetail;
  metadata: MetadataBundle | null;
  onUpdated: (next: AdminReleaseNoteDetail) => void;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.releaseNotes;
  const api = useAdminApi();
  const action = useAction();

  const [productId, setProductId] = useState(note.product.productId);
  const [version, setVersion] = useState(note.version);
  // The two addresses rather than the sentence, so the notice follows the language.
  const [savedNotice, setSavedNotice] = useState<{ next: string; previous: string } | null>(null);

  const dirty = productId !== note.product.productId || version !== note.version;

  const duplicateFromServer =
    action.error instanceof ApiError && action.error.conflict === 'duplicate'
      ? action.error
      : null;

  // The grammar first, then the server's "taken" - in that order, because a
  // version the grammar refuses was never sent.
  const versionError = !isValidReleaseNoteVersion(version)
    ? t.admin.rules.releaseNoteVersionRequirement
    : duplicateFromServer
      ? (duplicateFromServer.fieldError('version') ?? duplicateFromServer.message)
      : undefined;

  /** The chosen product's slug, from the note while it is the note's own product. */
  const productSlug =
    productId === note.product.productId
      ? note.product.slug
      : (metadata?.products.find((product) => product.productId === productId)?.slug ?? null);

  const submit = async () => {
    if (versionError) return;

    const previous = releaseNoteHref(note.product.slug, note.version);
    const result = await action.run(() =>
      api.updateReleaseNote(note.releaseNoteId, {
        productId,
        version,
        // Publishing is changed by its own panel; sending the current values
        // keeps this a pure properties update.
        publishAtUtc: note.publishAtUtc,
        isVisible: note.isVisible,
        rowVersion: note.rowVersion,
      }),
    );
    if (!result) return;

    onUpdated(result);
    // Read from the SAVED note: the product slug is the catalogue's, and the
    // server is the party that says what was stored.
    const next = releaseNoteHref(result.product.slug, result.version);
    if (next !== previous) {
      setSavedNotice({ next, previous });
    }
  };

  return (
    <div className="card properties-card">
      <div className="card-header">
        <h3>{words.common.properties}</h3>
      </div>

      {action.error && !duplicateFromServer && (
        <ErrorBanner error={action.error} onDismiss={action.clearError} />
      )}

      {savedNotice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">
            {words.properties.addressMoved(savedNotice.next, savedNotice.previous)}
          </div>
          <div className="notice-actions">
            <button
              type="button"
              className="button button-quiet"
              onClick={() => setSavedNotice(null)}
            >
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      <ProductSelect
        name="release-note-product"
        options={(metadata?.products ?? []).map((product) => ({
          id: product.productId,
          name: product.name,
          isActive: product.isActive,
        }))}
        selected={productId}
        onChange={(next) => {
          setProductId(next);
          // The server's "taken" verdict was about the pair that was sent.
          if (duplicateFromServer) action.clearError();
        }}
      />

      <ReleaseNoteVersionInput
        id="version"
        value={version}
        error={versionError}
        onChange={(next) => {
          setVersion(next);
          if (duplicateFromServer) action.clearError();
        }}
      />

      {/*
        The address being built, in full. Product and version are the note's
        URL, which is the consequence of editing them an author needs to see.
      */}
      <p className="faint properties-address">
        {richText(dirty ? words.properties.addressMoving : words.properties.address, {
          address: (
            <code>
              {productSlug && isValidReleaseNoteVersion(version)
                ? releaseNoteHref(productSlug, version)
                : '…'}
            </code>
          ),
        })}
      </p>

      <div className="button-row">
        <button
          type="button"
          className="button button-primary"
          onClick={() => void submit()}
          disabled={!dirty || action.running || Boolean(versionError)}
        >
          {action.running ? words.properties.saving : words.properties.save}
        </button>
        {dirty && <span className="unsaved">{words.properties.unsaved}</span>}
      </div>
    </div>
  );
}
